import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Calendar, Users, Globe2, Compass, Film, ChevronRight, Laugh, Drama, Heart, Moon } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "concert", label: "Concerts" },
  { key: "movie", label: "Movies", icon: Film },
  { key: "sports", label: "Sports" },
  { key: "gallery", label: "Galleries" },
  { key: "mixer", label: "Mixers" },
  { key: "nightlife", label: "Nightlife", icon: Moon },
  { key: "comedy", label: "Comedy", icon: Laugh },
  { key: "theatre", label: "Theatre", icon: Drama },
];

const CAT_COLOR = {
  comedy: "bg-yellow-500/20 text-yellow-200 border-yellow-400/30",
  nightlife: "bg-violet-500/20 text-violet-200 border-violet-400/30",
  theatre: "bg-rose-500/20 text-rose-200 border-rose-400/30",
  concert: "bg-pink-500/20 text-pink-200 border-pink-400/30",
  sports: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
  gallery: "bg-amber-500/20 text-amber-200 border-amber-400/30",
  mixer: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/30",
  movie: "bg-indigo-500/20 text-indigo-200 border-indigo-400/30",
};

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function Discover() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scope, setScope] = useState("local");
  const [cat, setCat] = useState("all");
  const catRef = useRef(null);
  const [catHint, setCatHint] = useState(true);
  const onCatScroll = () => {
    const el = catRef.current;
    if (el) setCatHint(el.scrollLeft <= 4 && el.scrollWidth > el.clientWidth + 4);
  };
  useEffect(() => {
    onCatScroll();
    window.addEventListener("resize", onCatScroll);
    return () => window.removeEventListener("resize", onCatScroll);
  }, []);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events", scope, cat],
    queryFn: async () => (await api.get("/events", { params: { scope, category: cat } })).data,
  });

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-4xl">Tonight's the plan</h1>
          <p className="text-slate-300 mt-1">Pick an event. Find someone to go with.</p>
        </div>
        <div className="glass rounded-full p-1 flex">
          <button data-testid="scope-local" onClick={() => setScope("local")}
            className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${scope === "local" ? "btn-gold" : "text-emerald-100/70"}`}>
            <Compass className="h-4 w-4" /> {user?.city || "Local"}
          </button>
          <button data-testid="scope-global" onClick={() => setScope("global")}
            className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all ${scope === "global" ? "btn-gold" : "text-emerald-100/70"}`}>
            <Globe2 className="h-4 w-4" /> Global
          </button>
        </div>
      </div>

      <div className="relative mt-6">
      <div ref={catRef} onScroll={onCatScroll} data-testid="category-bar"
        className="flex gap-2 overflow-x-auto no-scrollbar pb-1 snap-x snap-mandatory overscroll-x-contain touch-pan-x [-webkit-overflow-scrolling:touch]">
        {CATEGORIES.map((c) => (
          <button key={c.key} data-testid={`cat-${c.key}`} onClick={() => setCat(c.key)}
            className={`snap-start shrink-0 relative px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition-all ${
              cat === c.key
                ? "bg-amber-400/10 border-amber-400/70 text-white shadow-[0_0_0_1px_rgba(245,158,11,0.25)]"
                : "border-white/10 text-emerald-50/85 hover:text-white hover:border-white/25"
            }`}>
            {c.icon && <c.icon className="inline h-4 w-4 mr-1.5 -mt-0.5" />}{c.label}
            {cat === c.key && <span className="absolute left-1/2 -translate-x-1/2 bottom-1 h-0.5 w-5 rounded-full bg-pink-400/90" />}
          </button>
        ))}
      </div>
      <div data-testid="category-scroll-hint" aria-hidden="true"
        className={`pointer-events-none absolute right-0 top-0 bottom-1 w-20 flex items-center justify-end pr-1 bg-gradient-to-l from-[#0B130E] via-[#0B130E]/80 to-transparent transition-opacity duration-300 ${catHint ? "opacity-100" : "opacity-0"}`}>
        <ChevronRight className="h-5 w-5 text-amber-300/90 animate-[pulse_2s_ease-in-out_infinite]" />
      </div>
      </div>

      {isLoading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {[...Array(6)].map((_, i) => <div key={i} className="h-72 rounded-2xl bg-white/5 animate-pulse" />)}
        </div>
      ) : events.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center mt-10">
          <p className="text-emerald-100/70">No events {scope === "local" ? `within ${user?.radius_miles || 15} miles of ${user?.area_name || user?.city || "your area"}` : ""} yet. Try Global mode.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {events.map((ev, i) => (
            <button key={ev.id} data-testid={`event-card-${ev.id}`} onClick={() => navigate(`/events/${ev.id}`)}
              className="group text-left glass rounded-2xl overflow-hidden hover:border-amber-500/40 transition-all fade-up"
              style={{ animationDelay: `${Math.min(i * 0.04, 0.3)}s` }}>
              <div className="relative h-44 overflow-hidden">
                <img src={imgUrl(ev.cover_image)} alt={ev.title} loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"; }} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(11,19,14,.9), transparent 60%)" }} />
                <span className={`absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full border ${CAT_COLOR[ev.category] || "bg-white/10 text-white border-white/20"}`}>
                  {ev.category}
                </span>
                {ev.great_for_dates && (
                  <span data-testid={`great-badge-${ev.id}`} className="absolute top-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-400 text-emerald-950 flex items-center gap-1 shadow-lg">
                    <Heart className="h-3 w-3 fill-current" /> Great for dates
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-lg leading-tight line-clamp-1">{ev.title}</h3>
                <div className="flex items-center gap-1.5 text-xs text-amber-300/80 mt-2">
                  <Calendar className="h-3.5 w-3.5" /> {fmtDate(ev.date_time)}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-100/60 mt-1">
                  <MapPin className="h-3.5 w-3.5" /> {ev.venue}, {ev.city}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex -space-x-2">
                    {ev.attendee_preview.slice(0, 4).map((a) => (
                      <img key={a.user_id} src={imgUrl(a.photos?.[0] || a.picture)} alt={a.name}
                        className="h-7 w-7 rounded-full object-cover ring-2 ring-[#0B130E]" />
                    ))}
                  </div>
                  <span className="text-xs text-emerald-100/60 flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" /> {ev.attendee_count} interested
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
